import React, { useMemo, useState } from 'react';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Surface, Button } from '@/components/ui';
import styles from './ScopeGrid.module.css';
import type { Scope } from '@/types/roles';

interface ScopeGridProps {
  allScopes: Scope[];
  selectedIds: number[];
  onChange: (updatedIds: number[]) => void;
  disabled?: boolean;
}

export function formatScopeLabel(codename: string): string {
  if (!codename) return '';
  return codename
    .trim()
    .replace(/[_.-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      if (/^\d+$/.test(word)) return word;
      if (word.length <= 3 && word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

const ScopeGrid: React.FC<ScopeGridProps> = ({ allScopes, selectedIds, onChange, disabled = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('codename');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleScope = (scopeId: number) => {
    if (disabled) return;
    if (selectedIds.includes(scopeId)) {
      onChange(selectedIds.filter(id => id !== scopeId));
    } else {
      onChange([...selectedIds, scopeId]);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...allScopes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.codename.toLowerCase().includes(q) ||
        formatScopeLabel(s.codename).toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let valA: string | number = formatScopeLabel(a.codename).toLowerCase();
      let valB: string | number = formatScopeLabel(b.codename).toLowerCase();

      if (sortField === 'selected') {
        valA = selectedIds.includes(a.id) ? 1 : 0;
        valB = selectedIds.includes(b.id) ? 1 : 0;
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [allScopes, searchQuery, sortField, sortDir, selectedIds]);

  const pageSize = filteredAndSorted.length || 1;

  const columns: ColumnDef<Scope>[] = [
    {
      key: 'selected',
      header: 'Access',
      width: '80px',
      sortable: true,
      render: (scope) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(scope.id)}
          onChange={() => toggleScope(scope.id)}
          disabled={disabled}
          className={styles.checkbox}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      key: 'codename',
      header: 'Permission Name',
      sortable: true,
      render: (scope) => (
        <span className={styles['scope-name']}>{formatScopeLabel(scope.codename)}</span>
      )
    }
  ];

  const handleToggleAll = () => {
    if (disabled) return;
    const filteredIds = filteredAndSorted.map(s => s.id);
    const allFilteredAreSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));

    if (allFilteredAreSelected) {
      onChange(selectedIds.filter(id => !filteredIds.includes(id)));
    } else {
      const newSelected = new Set([...selectedIds, ...filteredIds]);
      onChange(Array.from(newSelected));
    }
  };

  const allFilteredSelected =
    filteredAndSorted.length > 0 && filteredAndSorted.every(s => selectedIds.includes(s.id));

  const toggleAllButton = (
    <Button
      size="sm"
      onClick={handleToggleAll}
      disabled={disabled || filteredAndSorted.length === 0}
    >
      {allFilteredSelected ? 'Deselect All' : 'Select All'}
    </Button>
  );

  return (
    <Surface className={styles.grid}>
      {/* `embedded` is what drops DataTable's own page frame and card, so this
          Surface is the only surface. It used to render standalone and undo the
          wrapper with an injected <style> block of !important overrides. */}
      <DataTable
        embedded
        data={filteredAndSorted}
        columns={columns}
        rowKey={(r) => r.id}
        totalCount={filteredAndSorted.length}
        loading={false}
        currentPage={1}
        totalPages={1}
        onPageChange={() => undefined}
        pageSize={pageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={handleSort}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search permissions…"
        searchRowSlot={toggleAllButton}
        compact
        hideFooter
        onRowClick={(row) => toggleScope(row.id)}
        emptyMessage="No permissions match your search."
      />
    </Surface>
  );
};

export default ScopeGrid;
